<script setup>
import { computed, onMounted, ref } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ConversationCard from 'dashboard/components-next/Conversation/ConversationCard/ConversationCard.vue';
import ContactAPI from 'dashboard/api/contacts';
import { dynamicTime } from 'shared/helpers/timeHelper';

const { t } = useI18n();
const route = useRoute();

const conversations = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);
const contactsById = useMapGetter('contacts/getContactById');
const stateInbox = useMapGetter('inboxes/getInboxById');
const accountLabels = useMapGetter('labels/getLabels');

const accountLabelsValue = computed(() => accountLabels.value);

const uiFlags = useMapGetter('contactConversations/getUIFlags');
const isFetching = computed(() => uiFlags.value.isFetching);

const contactConversations = computed(() =>
  conversations.value(route.params.contactId)
);

const timeline = ref([]);
const isFetchingTimeline = ref(false);

const eventIcon = type =>
  ({
    contact_created: 'i-lucide-user-plus',
    conversation_created: 'i-lucide-message-square-plus',
    note: 'i-lucide-notebook-pen',
    activity: 'i-lucide-activity',
  })[type] || 'i-lucide-circle';

onMounted(async () => {
  isFetchingTimeline.value = true;
  try {
    const { data } = await ContactAPI.getTimeline(route.params.contactId);
    timeline.value = data.payload || [];
  } finally {
    isFetchingTimeline.value = false;
  }
});
</script>

<template>
  <div
    v-if="isFetching || isFetchingTimeline"
    class="flex items-center justify-center py-10 text-n-slate-11"
  >
    <Spinner />
  </div>
  <div
    v-else-if="timeline.length || contactConversations.length"
    class="px-6 py-4"
  >
    <h4 class="mb-3 text-sm font-medium text-n-slate-12">
      {{ t('OPERATIONAL_WORKFLOW.TIMELINE.TITLE') }}
    </h4>
    <ol
      class="relative ltr:border-l rtl:border-r border-n-weak ltr:ml-2 rtl:mr-2"
    >
      <li
        v-for="event in timeline"
        :key="`${event.type}-${event.at}-${event.conversation_id || ''}`"
        class="relative pb-5 ltr:pl-6 rtl:pr-6"
      >
        <span
          class="absolute -top-0.5 ltr:-left-2.5 rtl:-right-2.5 flex size-5 items-center justify-center rounded-full bg-n-solid-3 text-n-slate-11"
        >
          <span :class="eventIcon(event.type)" class="size-3" />
        </span>
        <p class="mb-0 text-sm text-n-slate-12">{{ event.title }}</p>
        <p
          v-if="event.description"
          class="mt-1 mb-0 text-xs line-clamp-3 text-n-slate-11"
        >
          {{ event.description }}
        </p>
        <p class="mt-1 mb-0 text-xs text-n-slate-10">
          {{ dynamicTime(event.at)
          }}<span v-if="event.actor">{{ ` · ${event.actor}` }}</span>
        </p>
      </li>
    </ol>
    <h4
      v-if="contactConversations.length"
      class="mt-3 mb-2 text-sm font-medium text-n-slate-12"
    >
      {{ t('OPERATIONAL_WORKFLOW.TIMELINE.CONVERSATIONS') }}
    </h4>
    <div class="divide-y divide-n-strong">
      <ConversationCard
        v-for="conversation in contactConversations"
        :key="conversation.id"
        :conversation="conversation"
        :contact="contactsById(conversation.meta.sender.id)"
        :state-inbox="stateInbox(conversation.inboxId)"
        :account-labels="accountLabelsValue"
        class="rounded-none hover:rounded-xl hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3"
      />
    </div>
  </div>
  <p v-else class="px-6 py-10 text-sm leading-6 text-center text-n-slate-11">
    {{ t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.EMPTY_STATE') }}
  </p>
</template>
